import React, { useState } from 'react';
import { LayerWithVisualization, LayerType, ActivationType } from '../../types/modelBuilder';
import { useModelBuilder } from '../../context/ModelBuilderContext';
import TensorVisualizer from './TensorVisualizer';
import '../../styles/modelBuilder.css';

interface LayerConfigPanelProps {
  layer: LayerWithVisualization | null;
}

const ACTIVATION_OPTIONS: ActivationType[] = [
  'relu',
  'sigmoid',
  'tanh',
  'softmax',
  'elu',
  'leaky_relu',
  'gelu',
  'swish',
  'silu',
  'mish',
  'hard_swish',
  'hard_sigmoid',
  'prelu',
];

export const LayerConfigPanel: React.FC<LayerConfigPanelProps> = ({ layer }) => {
  const { updateLayer, removeLayer, layers } = useModelBuilder();
  const [editingName, setEditingName] = useState(false);

  if (!layer) {
    return (
      <div className="layer-config-panel">
        <div className="empty-state">
          <p>Select a layer to configure</p>
        </div>
      </div>
    );
  }

  const handleNameChange = (newName: string) => {
    if (newName.trim()) {
      updateLayer(layer.id, { name: newName });
    }
  };

  const handleParamChange = (key: string, value: any) => {
    updateLayer(layer.id, { [key]: value });
  };

  const handleForwardChange = (forward: string) => {
    updateLayer(layer.id, { forward });
  };

  const handleInputsChange = (inputs: string[]) => {
    updateLayer(layer.id, { inputs });
  };

  // Get available layers for inputs selector (excluding current layer)
  const availableLayers = layers.filter(l => l.id !== layer.id);

  const renderConfigInputs = () => {
    switch (layer.type) {
      case 'conv2d':
      case 'conv1d':
      case 'conv3d':
      case 'depthwise_separable_conv2d':
      case 'conv_transpose2d':
        return (
          <>
            <div className="config-group">
              <label>In Channels</label>
              <input
                type="number"
                value={layer.in_channels || 3}
                onChange={(e) => handleParamChange('in_channels', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Out Channels</label>
              <input
                type="number"
                value={layer.out_channels || 32}
                onChange={(e) => handleParamChange('out_channels', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Kernel Size</label>
              <input
                type="number"
                value={typeof layer.kernel_size === 'number' ? layer.kernel_size : (layer.kernel_size as number[])?.[0] || 3}
                onChange={(e) => handleParamChange('kernel_size', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Stride</label>
              <input
                type="number"
                value={typeof layer.stride === 'number' ? layer.stride : (layer.stride as number[])?.[0] || 1}
                onChange={(e) => handleParamChange('stride', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Padding</label>
              <input
                type="number"
                value={typeof layer.padding === 'number' ? layer.padding : (layer.padding === 'same' ? 1 : 0)}
                onChange={(e) => handleParamChange('padding', parseInt(e.target.value))}
                min="0"
              />
            </div>
            {layer.type === 'conv_transpose2d' && (
              <div className="config-group">
                <label>Output Padding</label>
                <input
                  type="number"
                  value={layer.output_padding || 0}
                  onChange={(e) => handleParamChange('output_padding', parseInt(e.target.value))}
                  min="0"
                />
              </div>
            )}
          </>
        );

      case 'linear':
        return (
          <>
            <div className="config-group">
              <label>In Features</label>
              <input
                type="number"
                value={layer.in_features || 128}
                onChange={(e) => handleParamChange('in_features', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Out Features</label>
              <input
                type="number"
                value={layer.out_features || 64}
                onChange={(e) => handleParamChange('out_features', parseInt(e.target.value))}
                min="1"
              />
            </div>
          </>
        );

      case 'batch_norm2d':
      case 'batch_norm':
      case 'instance_norm2d':
        return (
          <div className="config-group">
            <label>Num Features</label>
            <input
              type="number"
              value={layer.num_features || 32}
              onChange={(e) => handleParamChange('num_features', parseInt(e.target.value))}
              min="1"
            />
          </div>
        );

      case 'layer_norm':
        return (
          <div className="config-group">
            <label>Normalized Shape</label>
            <input
              type="number"
              value={typeof layer.normalized_shape === 'number' ? layer.normalized_shape : (layer.normalized_shape as number[])?.[0] || 256}
              onChange={(e) => handleParamChange('normalized_shape', parseInt(e.target.value))}
              min="1"
            />
          </div>
        );

      case 'group_norm':
        return (
          <>
            <div className="config-group">
              <label>Num Groups</label>
              <input
                type="number"
                value={layer.num_groups || 32}
                onChange={(e) => handleParamChange('num_groups', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Num Channels</label>
              <input
                type="number"
                value={layer.num_channels || 32}
                onChange={(e) => handleParamChange('num_channels', parseInt(e.target.value))}
                min="1"
              />
            </div>
          </>
        );

      case 'dropout':
        return (
          <div className="config-group">
            <label>Dropout Rate (p)</label>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={layer.p || 0.5}
              onChange={(e) => handleParamChange('p', parseFloat(e.target.value))}
            />
            <span className="value-display">{(layer.p || 0.5).toFixed(1)}</span>
          </div>
        );

      case 'adaptive_avg_pool2d':
      case 'adaptive_max_pool2d':
        return (
          <div className="config-group">
            <label>Output Size</label>
            <input
              type="number"
              value={typeof layer.output_size === 'number' ? layer.output_size : (layer.output_size as number[])?.[0] || 1}
              onChange={(e) => handleParamChange('output_size', parseInt(e.target.value))}
              min="1"
            />
          </div>
        );

      case 'lstm':
      case 'gru':
      case 'rnn':
        return (
          <>
            <div className="config-group">
              <label>Input Size</label>
              <input
                type="number"
                value={layer.input_size || 128}
                onChange={(e) => handleParamChange('input_size', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Hidden Size</label>
              <input
                type="number"
                value={layer.hidden_size || 128}
                onChange={(e) => handleParamChange('hidden_size', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Num Layers</label>
              <input
                type="number"
                value={layer.num_layers || 1}
                onChange={(e) => handleParamChange('num_layers', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group checkbox">
              <input
                type="checkbox"
                id="bidirectional"
                checked={layer.bidirectional || false}
                onChange={(e) => handleParamChange('bidirectional', e.target.checked)}
              />
              <label htmlFor="bidirectional">Bidirectional</label>
            </div>
          </>
        );

      case 'multihead_attention':
        return (
          <>
            <div className="config-group">
              <label>Embed Dim</label>
              <input
                type="number"
                value={layer.embed_dim || 256}
                onChange={(e) => handleParamChange('embed_dim', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Num Heads</label>
              <input
                type="number"
                value={layer.num_heads || 8}
                onChange={(e) => handleParamChange('num_heads', parseInt(e.target.value))}
                min="1"
              />
            </div>
          </>
        );

      case 'embedding':
        return (
          <>
            <div className="config-group">
              <label>Num Embeddings</label>
              <input
                type="number"
                value={layer.num_embeddings || 1000}
                onChange={(e) => handleParamChange('num_embeddings', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Embedding Dim</label>
              <input
                type="number"
                value={layer.embedding_dim || 128}
                onChange={(e) => handleParamChange('embedding_dim', parseInt(e.target.value))}
                min="1"
              />
            </div>
          </>
        );

      case 'upsample':
        return (
          <>
            <div className="config-group">
              <label>Scale Factor</label>
              <input
                type="number"
                value={typeof layer.scale_factor === 'number' ? layer.scale_factor : (layer.scale_factor as number[])?.[0] || 2}
                onChange={(e) => handleParamChange('scale_factor', parseInt(e.target.value))}
                min="1"
              />
            </div>
            <div className="config-group">
              <label>Mode</label>
              <select
                value={layer.mode || 'nearest'}
                onChange={(e) => handleParamChange('mode', e.target.value)}
              >
                <option value="nearest">Nearest</option>
                <option value="linear">Linear</option>
                <option value="bilinear">Bilinear</option>
                <option value="bicubic">Bicubic</option>
                <option value="trilinear">Trilinear</option>
              </select>
            </div>
          </>
        );

      case 'concatenate':
        return (
          <div className="config-group">
            <label>Dim</label>
            <input
              type="number"
              value={layer.dim || 1}
              onChange={(e) => handleParamChange('dim', parseInt(e.target.value))}
              min="0"
            />
          </div>
        );

      case 'add':
      case 'multiply':
      case 'global_avg_pool':
      case 'global_max_pool':
        return <div className="config-group"><p>No parameters needed for this layer type.</p></div>;

      default:
        return null;
    }
  };

  return (
    <div className="layer-config-panel">
      <div className="panel-header">
        <div className="layer-name-edit">
          {editingName ? (
            <input
              autoFocus
              type="text"
              defaultValue={layer.name || layer.id}
              onBlur={(e) => {
                handleNameChange(e.target.value);
                setEditingName(false);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleNameChange(e.currentTarget.value);
                  setEditingName(false);
                }
              }}
              className="name-input"
            />
          ) : (
            <h3 onClick={() => setEditingName(true)}>{layer.name || layer.id}</h3>
          )}
        </div>
        <span className="layer-type-badge">{layer.type}</span>
      </div>

      <div className="panel-section">
        <h4>Inputs</h4>
        <div className="config-group">
          <label>Connected Layers</label>
          <select
            multiple
            value={layer.inputs || []}
            onChange={(e) => {
              const selected = Array.from(e.target.selectedOptions, option => option.value);
              handleInputsChange(selected);
            }}
            style={{ minHeight: '100px' }}
          >
            <option value="input">input</option>
            {availableLayers.map(l => (
              <option key={l.id} value={l.id}>{l.name || l.id}</option>
            ))}
          </select>
          <small>Hold Ctrl/Cmd to select multiple inputs</small>
        </div>
      </div>

      <div className="panel-section">
        <h4>Tensor Shapes</h4>
        <div className="tensor-info">
          <div className="tensor-display">
            <label>Input Shape</label>
            <TensorVisualizer shape={layer.inputShape || []} color="#10B981" size="small" />
          </div>
          <div className="tensor-display">
            <label>Output Shape</label>
            <TensorVisualizer shape={layer.outputShape || []} color="#3B82F6" size="small" />
          </div>
        </div>
      </div>

      <div className="panel-section">
        <h4>Layer Parameters</h4>
        <div className="config-inputs">
          {renderConfigInputs()}
        </div>
      </div>

      <div className="panel-section">
        <h4>Forward Code</h4>
        <div className="config-group">
          <label>Python Forward Pass Code</label>
          <textarea
            value={layer.forward || ''}
            onChange={(e) => handleForwardChange(e.target.value)}
            rows={6}
            style={{ width: '100%', fontFamily: 'monospace', fontSize: '12px' }}
            placeholder="e.g., x = F.max_pool2d(F.swish(self.conv1(x)), 2, stride=2)"
          />
          <small>Use \n for multi-line code. Available: F.relu, F.swish, F.mish, F.gelu, F.leaky_relu, etc.</small>
        </div>
      </div>

      <div className="panel-footer">
        <button
          className="btn-delete"
          onClick={() => removeLayer(layer.id)}
        >
          🗑️ Delete Layer
        </button>
      </div>
    </div>
  );
};

export default LayerConfigPanel;
